import { Button } from "@/components/common/Button/Button"
import { Logo } from "@/components/common/Logo/Logo"
import { Typography } from "@/components/common/Typography/Typography"

import { toast } from "@/components/common/Toast"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { performSignOut } from "@/services/api/performSignOut"
import { URLS } from "@/utils/urls.const"
import { CheckboxField } from "@aws-amplify/ui-react"
import { useQueryClient } from "@tanstack/react-query"
import { updateUserAttribute } from "aws-amplify/auth"
import React from "react"
import { useNavigate } from "react-router"

interface TermsAndConditionsProps {
  setTermsAccepted: (accepted: boolean) => void
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  setTermsAccepted,
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [newsletterSignup, setNewsletterSignup] = React.useState(false)
  const { translate } = useTranslation()
  const [termsAccepted, setTermsAcceptedState] = React.useState(false)

  const handleAccept = async () => {
    await updateUserAttribute({
      userAttribute: {
        attributeKey: "custom:tcAcknowledged",
        value: "true",
      },
    })

    if (newsletterSignup) {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: "custom:newsletterSignup",
          value: "true",
        },
      })
    }

    setTermsAccepted(true) // Update the state immediately
    navigate("/")
  }

  const handleLogout = async () => {
    const outcome = await performSignOut(queryClient)
    if (outcome === "failed") {
      toast.error(translate("USER_ERROR_SIGNOUT"))
      return
    }
    if (outcome === "signed-out") navigate("/")
  }

  return (
    <div className="h-screen w-screen">
      <div className="flex min-h-full flex-col items-center justify-center">
        <div className="flex flex-col items-center justify-center rounded-2xl bg-grey3 px-12 py-20">
          <div className="mb-8 flex h-10 w-60 items-center justify-center">
            <Logo />
          </div>
          <Typography
            className="mb-6 max-w-96 text-center"
            as="p"
            variant="titleM"
          >
            {translate("LOGIN/TERMS")}
          </Typography>
          <Typography
            className="max-w-96 py-4 text-center"
            as="p"
            variant="textS"
          >
            {translate("POST_SIGNUP/CTA_ACK_TERMS")}{" "}
            <a
              href={URLS.INFO_BOX.AGBS}
              style={{ color: "#4493f8" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate("LOGIN/TERMS")}
            </a>{" "}
            {translate("POST_SIGNUP/CTA_ACK_TERMS_MID")}{" "}
            <a
              href={URLS.INFO_BOX.DATA_POLICY}
              style={{ color: "#4493f8" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate("LOGIN/PRIVACY_POLICY")}
            </a>{" "}
            {translate("POST_SIGNUP/CTA_ACK_TERMS_POST")}
          </Typography>
          <div className="flex w-full flex-col"></div>
          <div className="flex w-full flex-col">
            <div className="flex items-start gap-2 py-4">
              <CheckboxField
                name="terms-acceptance"
                value="yes"
                required
                label={
                  <span className="text-sm">
                    {translate("POST_SIGNUP/ACCEPT")}
                  </span>
                }
                onClick={() => setTermsAcceptedState(!termsAccepted)}
              />
            </div>

            <div className="flex items-start gap-2 py-2">
              <CheckboxField
                name="custom:newsletterSignup"
                value="yes"
                label={
                  <span className="text-sm">
                    {translate("POST_SIGNUP/NEWSLETTER")}
                  </span>
                }
                onClick={() => setNewsletterSignup(!newsletterSignup)}
              />
            </div>

            <div className="flex flex-col gap-4 pt-8">
              <Button
                variantColor="yellow"
                title={translate("POST_SIGNUP/CONTINUE")}
                label={translate("POST_SIGNUP/CONTINUE")}
                onClick={handleAccept}
                variantSize="medium"
                isFullWidth={false}
                isDisabled={!termsAccepted}
              />

              <Button
                variantColor="dark"
                title={translate("POST_SIGNUP/NOT_ACCEPT")}
                label={translate("POST_SIGNUP/NOT_ACCEPT")}
                onClick={handleLogout}
                variantSize="small"
                isFullWidth={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsAndConditions
